import { useCreateIntegrationConnection } from "@/api/integrations";
import { usePostNangoConnectionSession } from "@/api/integrations/nango";
import analytics from "@/utils/analytics";
import { Integration, IntegrationConnection } from "@cohesive/api-client";

import Nango, { ConnectionConfig } from "@nangohq/frontend";
import { useMutation } from "@tanstack/react-query";

const HUBSPOT_SCOPES = [
  "crm.objects.companies.read",
  "crm.objects.companies.write",
  "crm.objects.contacts.read",
  "crm.objects.contacts.write",
  "crm.objects.deals.read",
  "crm.objects.deals.write",
  "crm.objects.leads.read",
  "crm.objects.leads.write",
  "crm.objects.orders.read",
  "crm.objects.orders.write",
  "crm.objects.owners.read",
  "crm.objects.quotes.read",
  "crm.objects.quotes.write",
  "crm.objects.users.read",
  "oauth",
  "sales-email-read",
  "tickets",
];

const SLACK_SCOPES = [
  "channels:history",
  "channels:read",
  "channels:write",
  "chat:write",
  "files:read",
  "files:write",
  "im:history",
  "im:read",
  "im:write",
  "mpim:history",
  "mpim:read",
  "mpim:write",
  "users:read",
  "users:read.email",
  "search:read",
  "reminders:read",
  "reminders:write",
  "reactions:read",
  "reactions:write",
  "groups:read",
];

export function getNangoConnectionConfig(
  platform: string,
): ConnectionConfig | null {
  switch (platform) {
    case "slack":
      return {
        authorization_params: {
          user_scope: SLACK_SCOPES.join(","),
        },
      };
    case "hubspot":
      return {
        authorization_params: {
          optional_scope: HUBSPOT_SCOPES.join(" "),
        },
      };
    default:
      return null;
  }
}

export function useConnectNango() {
  const nangoMutation = usePostNangoConnectionSession();
  const createIntegrationConnection = useCreateIntegrationConnection();

  const connectNangoMutation = useMutation<
    IntegrationConnection,
    Error,
    Integration
  >({
    mutationFn: async (integration: Integration) => {
      const response = await nangoMutation.mutateAsync({
        allowed_integrations: [integration.provider_id],
      });

      if (!response.data.data.token) {
        throw new Error("No token received from Nango");
      }

      const nango = new Nango({
        connectSessionToken: response.data.data.token,
      });

      const connectionConfig = getNangoConnectionConfig(integration.platform);

      if (connectionConfig) {
        const connection = await nango.auth(
          integration.provider_id,
          connectionConfig,
        );

        const newConnection = await createIntegrationConnection.mutateAsync({
          provider: integration.provider,
          providerId: connection.connectionId,
          createdAt: integration.created_at,
          integration: integration,
        });

        analytics.trackUserConnectionAuthorized({
          integrationId: integration.id,
          platform: integration.platform,
          provider: "nango",
        });

        return newConnection;
      }

      return new Promise((resolve) => {
        nango.openConnectUI({
          onEvent: async (event) => {
            if (event.type === "connect") {
              const { connectionId } = event.payload;

              const newConnection =
                await createIntegrationConnection.mutateAsync({
                  provider: integration.provider,
                  providerId: connectionId,
                  createdAt: integration.created_at,
                  integration: integration,
                });

              analytics.trackUserConnectionAuthorized({
                integrationId: integration.id,
                platform: integration.platform,
                provider: "nango",
              });

              resolve(newConnection);
            }
          },
        });
      });
    },
  });

  return connectNangoMutation;
}
